import {isClothingDepartmentName} from '../../../models/clothing-department';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import { SidebarService } from '../../../services/side-bar.service';
import { AdminService } from '../../../services/admin.service';
import { ClothingService } from '../../../services/clothing.service';
import { ClothingErrorService } from '../../../services/clothing-error.service';
import {
  ClothingOption,
  ClothingStorekeeperAssignment,
  ClothingStorekeeperAssignmentPayload,
  ClothingUserOption
} from '../../../models/clothing.model';
import { CLOTHING_MENU_OPTIONS } from './clothing.constants';

interface ClothingDepartmentOption extends ClothingOption {
  departmentMarkerCode?: string;
}

interface ClothingAssignmentCell {
  key: string;
  storekeeperId: string;
  assigned: boolean;
}

interface ClothingAssignmentRow {
  department: ClothingOption;
  assignmentId: string;
  assignedStorekeeperId: string;
  cells: ClothingAssignmentCell[];
}

interface ClothingAssignmentData {
  assignments: ClothingStorekeeperAssignment[];
  departments: ClothingOption[];
  storekeepers: ClothingUserOption[];
}

interface ClothingAssignmentIndex {
  [groupId: string]: ClothingStorekeeperAssignment;
}

@Component({
  selector: 'app-clothing-assignments',
  templateUrl: './clothing-assignments.component.html',
  styleUrls: ['./clothing-assignments.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminClothingAssignmentsComponent implements OnInit, OnDestroy {
  @Input() embedded = false;
  storekeepers: ClothingUserOption[] = [];
  rows: ClothingAssignmentRow[] = [];
  errorMessage = '';
  isLoading = false;
  savingCellKey = '';
  private destroy$ = new Subject<void>();

  constructor(
    private adminService: AdminService,
    private clothingService: ClothingService,
    private clothingErrorService: ClothingErrorService,
    private sidebarService: SidebarService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.sidebarService.setSelectedMenuOption(CLOTHING_MENU_OPTIONS.settings);
    this.loadMatrix();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasRows(): boolean {
    return this.rows.length > 0;
  }

  get hasStorekeepers(): boolean {
    return this.storekeepers.length > 0;
  }

  assignStorekeeper(row: ClothingAssignmentRow, cell: ClothingAssignmentCell) {
    this.clearMessages();
    this.savingCellKey = cell.key;
    this.getAssignmentRequest(row, cell)
      .takeUntil(this.destroy$)
      .subscribe(
        (assignment) => this.handleAssignmentSaved(row, cell, assignment),
        (error: HttpErrorResponse) => this.handleAssignmentFailed(error)
      );
  }

  trackByRow(index: number, row: ClothingAssignmentRow): string {
    return row.department.id;
  }

  trackByStorekeeper(index: number, storekeeper: ClothingUserOption): string {
    return storekeeper.id;
  }

  trackByCell(index: number, cell: ClothingAssignmentCell): string {
    return cell.key;
  }

  isSavingCell(cell: ClothingAssignmentCell): boolean {
    return this.savingCellKey === cell.key;
  }

  private loadMatrix() {
    this.isLoading = true;
    Observable.forkJoin(
      this.clothingService.getStorekeeperAssignments(),
      this.adminService.getGroupsWithDepartmentMarker(),
      this.clothingService.getStorekeeperOptions()
    )
      .takeUntil(this.destroy$)
      .subscribe(
        (response) => this.handleMatrixLoaded(this.createMatrixData(response)),
        (error: HttpErrorResponse) => this.handleMatrixFailed(error)
      );
  }

  private createMatrixData(response: any[]): ClothingAssignmentData {
    return {
      assignments: response[0] || [],
      departments: this.extractDepartments(response[1] as HttpResponse<ClothingDepartmentOption[]>),
      storekeepers: this.sortUsers(response[2] as ClothingUserOption[])
    };
  }

  private extractDepartments(
    response: HttpResponse<ClothingDepartmentOption[]>
  ): ClothingOption[] {
    var departments = response.body || [];

    return this.sortByName(departments.filter((department) => this.hasDepartmentMarker(department)));
  }

  private handleMatrixLoaded(data: ClothingAssignmentData) {
    this.storekeepers = data.storekeepers;
    this.rows = this.createRows(data.departments, data.storekeepers, data.assignments);
    this.isLoading = false;
    this.markForCheck();
  }

  private handleMatrixFailed(error: HttpErrorResponse) {
    this.isLoading = false;
    this.showHttpError(error);
  }

  private createRows(
    departments: ClothingOption[],
    storekeepers: ClothingUserOption[],
    assignments: ClothingStorekeeperAssignment[]
  ): ClothingAssignmentRow[] {
    var assignmentIndex = this.createAssignmentIndex(assignments);

    return departments.map((department) => this.createRow(department, storekeepers, assignmentIndex));
  }

  private createAssignmentIndex(
    assignments: ClothingStorekeeperAssignment[]
  ): ClothingAssignmentIndex {
    var index: ClothingAssignmentIndex = {};

    this.filterActiveAssignments(assignments)
      .forEach((assignment) => index[assignment.groupId] = assignment);
    return index;
  }

  private filterActiveAssignments(
    assignments: ClothingStorekeeperAssignment[]
  ): ClothingStorekeeperAssignment[] {
    return assignments.filter((assignment) => assignment.active);
  }

  private createRow(
    department: ClothingOption,
    storekeepers: ClothingUserOption[],
    assignmentIndex: ClothingAssignmentIndex
  ): ClothingAssignmentRow {
    var assignment = assignmentIndex[department.id];

    return {
      department: department,
      assignmentId: this.resolveAssignmentId(assignment),
      assignedStorekeeperId: this.resolveStorekeeperId(assignment),
      cells: storekeepers.map((storekeeper) => this.createCell(department.id, storekeeper.id, assignment))
    };
  }

  private createCell(
    departmentId: string,
    storekeeperId: string,
    assignment: ClothingStorekeeperAssignment
  ): ClothingAssignmentCell {
    return {
      key: departmentId + '::' + storekeeperId,
      storekeeperId: storekeeperId,
      assigned: this.isAssignedStorekeeper(storekeeperId, assignment)
    };
  }

  private isAssignedStorekeeper(
    storekeeperId: string,
    assignment: ClothingStorekeeperAssignment
  ): boolean {
    if (!assignment) {
      return false;
    }
    return assignment.storekeeperUserId === storekeeperId;
  }

  private resolveAssignmentId(assignment: ClothingStorekeeperAssignment): string {
    if (!assignment) {
      return '';
    }
    return assignment.assignmentId;
  }

  private resolveStorekeeperId(assignment: ClothingStorekeeperAssignment): string {
    if (!assignment) {
      return '';
    }
    return assignment.storekeeperUserId;
  }

  private getAssignmentRequest(
    row: ClothingAssignmentRow,
    cell: ClothingAssignmentCell
  ): Observable<Object> {
    if (!row.assignmentId.length) {
      return this.createAssignment(row.department.id, cell.storekeeperId);
    }
    if (row.assignedStorekeeperId === cell.storekeeperId) {
      return this.clothingService.deactivateStorekeeperAssignment(row.assignmentId);
    }
    return this.updateAssignment(row.assignmentId, row.department.id, cell.storekeeperId);
  }

  private createAssignment(groupId: string, storekeeperUserId: string) {
    return this.clothingService.createStorekeeperAssignment(
      this.createPayload(groupId, storekeeperUserId)
    );
  }

  private updateAssignment(
    assignmentId: string,
    groupId: string,
    storekeeperUserId: string
  ) {
    return this.clothingService.updateStorekeeperAssignment(
      assignmentId,
      this.createPayload(groupId, storekeeperUserId)
    );
  }

  private createPayload(
    groupId: string,
    storekeeperUserId: string
  ): ClothingStorekeeperAssignmentPayload {
    return {
      groupId: groupId,
      storekeeperUserId: storekeeperUserId,
      active: true
    };
  }

  private handleAssignmentSaved(
    row: ClothingAssignmentRow,
    cell: ClothingAssignmentCell,
    assignment: Object
  ) {
    if (assignment && (assignment as ClothingStorekeeperAssignment).assignmentId) {
      this.applyAssignedStorekeeper(row, assignment as ClothingStorekeeperAssignment);
      return this.finishAssignmentChange();
    }
    this.clearAssignedStorekeeper(row);
    this.finishAssignmentChange();
  }

  private applyAssignedStorekeeper(
    row: ClothingAssignmentRow,
    assignment: ClothingStorekeeperAssignment
  ) {
    row.assignmentId = assignment.assignmentId;
    row.assignedStorekeeperId = assignment.storekeeperUserId;
    this.syncRowCells(row);
  }

  private clearAssignedStorekeeper(row: ClothingAssignmentRow) {
    row.assignmentId = '';
    row.assignedStorekeeperId = '';
    this.syncRowCells(row);
  }

  private syncRowCells(row: ClothingAssignmentRow) {
    row.cells.forEach((cell) => cell.assigned = row.assignedStorekeeperId === cell.storekeeperId);
  }

  private finishAssignmentChange() {
    this.savingCellKey = '';
    this.markForCheck();
  }

  private handleAssignmentFailed(error: HttpErrorResponse) {
    this.savingCellKey = '';
    this.showHttpError(error);
  }

  private sortByName<T extends ClothingOption>(items: T[]): T[] {
    return items.slice().sort((left, right) => {
      if (left.name < right.name) {
        return -1;
      }
      if (left.name > right.name) {
        return 1;
      }
      return 0;
    });
  }

  private sortUsers(items: ClothingUserOption[]): ClothingUserOption[] {
    return items.slice().sort((left, right) => {
      if (left.fullName < right.fullName) {
        return -1;
      }
      if (left.fullName > right.fullName) {
        return 1;
      }
      return 0;
    });
  }

  private clearMessages() {
    this.errorMessage = '';
  }

  private showHttpError(error: HttpErrorResponse) {
    this.errorMessage = this.resolveErrorMessage(error);
    this.markForCheck();
  }

  private hasDepartmentMarker(department: ClothingDepartmentOption): boolean {
    var marker = this.resolveDepartmentMarker(department);

    if (!marker.length) {
      return false;
    }
    return isClothingDepartmentName(department.name);
  }

  private resolveDepartmentMarker(department: ClothingDepartmentOption): string {
    if (!department || !department.departmentMarkerCode) {
      return '';
    }
    return department.departmentMarkerCode.trim();
  }

  private resolveErrorMessage(error: HttpErrorResponse): string {
    return this.clothingErrorService.resolveDefaultMessage(error);
  }

  private markForCheck() {
    this.changeDetectorRef.markForCheck();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/clothing/clothing-assignments.component.ts